import { Component } from '@theme/component';

// Official Kiwi SDK and public loadSizing API. Kept off the initial product load.
const KIWI_SCRIPT = 'https://app.kiwisizing.com/web/js/dist/kiwiSizing/plugin/SizingPlugin.prod.js';
const LOAD_TIMEOUT = 20000;
let sdkPromise;

export function ensureKiwiSDK(shop) {
  if (window.ks?.loadSizing && window.ks?.setShopID) return Promise.resolve(window.ks);
  if (sdkPromise) return sdkPromise;
  window.disableDefaultKiwiSizingLoad = true;
  window.KiwiSizing = { ...window.KiwiSizing, shop };
  const promise = new Promise((resolve, reject) => {
    let script = Array.from(document.scripts).find((node) => node.src.startsWith(KIWI_SCRIPT) && !node.dataset.echtKiwiFailed);
    const owned = !script;
    if (!script) {
      script = document.createElement('script');
      script.src = KIWI_SCRIPT;
      script.async = true;
      script.dataset.echtKiwiSdk = '';
    }
    const finish = (error) => {
      clearTimeout(timer);
      script.removeEventListener('load', loaded);
      script.removeEventListener('error', failed);
      if (error) {
        script.dataset.echtKiwiFailed = 'true';
        if (owned) script.remove();
        reject(error);
      } else resolve(window.ks);
    };
    const loaded = () => finish(window.ks?.loadSizing && window.ks?.setShopID ? null : new Error('Kiwi SDK unavailable'));
    const failed = () => finish(new Error('Kiwi SDK could not load'));
    const timer = setTimeout(() => finish(new Error('Kiwi SDK timed out')), LOAD_TIMEOUT);
    script.addEventListener('load', loaded);
    script.addEventListener('error', failed);
    if (owned) document.head.append(script);
  });
  sdkPromise = promise;
  promise.catch(() => { if (sdkPromise === promise) sdkPromise = undefined; });
  return promise;
}

/** The native drawer owns focus/Escape. Kiwi owns chart matching and units. */
export default class EchtSizeGuide extends Component {
  #teardown = [];
  #attempt = 0;
  #timer;
  #state = 'idle';
  #contextKey;

  connectedCallback() {
    super.connectedCallback();
    for (const off of this.#teardown) off();
    this.#teardown = [];
    const dialog = this.querySelector('dialog');
    if (!dialog) return;
    const listen = (element, event, callback) => {
      element.addEventListener(event, callback);
      this.#teardown.push(() => element.removeEventListener(event, callback));
    };
    const scope = this.closest('[id^="shopify-section-"]') ?? document;
    for (const trigger of scope.querySelectorAll('[data-echt-size-guide]')) {
      listen(trigger, 'click', (event) => {
        event.preventDefault();
        if (!dialog.open) dialog.showModal();
        this.#loadChart();
      });
    }
    for (const closer of this.querySelectorAll('[data-echt-size-guide-close]')) {
      listen(closer, 'click', () => dialog.close());
    }
    listen(dialog, 'click', (event) => { if (event.target === dialog) dialog.close(); });
    const retry = this.querySelector('[data-echt-kiwi-retry]');
    if (retry) listen(retry, 'click', () => this.#loadChart(true));
  }

  #setState(state) {
    this.#state = state;
    const status = this.querySelector('[data-echt-kiwi-status]');
    const retry = this.querySelector('[data-echt-kiwi-retry]');
    const mount = this.querySelector('[data-echt-kiwi-mount]');
    if (status) status.textContent = state === 'loading'
      ? 'Loading your size guide…'
      : state === 'error' ? 'We couldn’t load a size guide for this product. Please try again.' : '';
    if (retry) retry.hidden = state !== 'error';
    if (mount) {
      mount.setAttribute('aria-busy', String(state === 'loading'));
      mount.hidden = state === 'error';
    }
  }

  async #loadChart(force = false) {
    const productJSON = this.querySelector('[data-echt-kiwi-product]')?.textContent || '{}';
    const contextKey = `${this.dataset.shop}|${productJSON}`;
    if (contextKey !== this.#contextKey) {
      this.#contextKey = contextKey;
      this.#state = 'idle';
    }
    if (this.#state === 'loading' || (this.#state === 'ready' && !force)) return;
    const original = this.querySelector('[data-echt-kiwi-mount]');
    if (!original) return;
    const attempt = ++this.#attempt;
    clearTimeout(this.#timer);
    // A late response must never replace a successful retry or another product.
    const mount = original.cloneNode(false);
    original.replaceWith(mount);
    this.#setState('loading');
    let settled = false;
    const finish = (success) => {
      if (settled || attempt !== this.#attempt || !this.isConnected) return;
      settled = true;
      clearTimeout(this.#timer);
      this.#setState(success ? 'ready' : 'error');
    };
    try {
      const productData = JSON.parse(productJSON);
      if (!productData.product || !this.dataset.shop) throw new Error('Product context missing');
      const sdk = await ensureKiwiSDK(this.dataset.shop);
      if (attempt !== this.#attempt || !this.isConnected) return;
      sdk.setShopID(this.dataset.shop);
      this.#timer = setTimeout(() => finish(false), LOAD_TIMEOUT);
      sdk.loadSizing({
        productData,
        options: {
          el: mount,
          calculatorEl: mount,
          displayMode: 0,
          layoutType: 2,
          injectionOrder: 2,
          calculatorInjectionOrder: 3,
          calculatorDisplayMode: 0,
        },
        // SDK callback is renderSuccess + a no-op helper, not a chart payload.
        loaded: (success) => finish(success === true),
      });
    } catch {
      finish(false);
    }
  }

  disconnectedCallback() {
    ++this.#attempt;
    clearTimeout(this.#timer);
    if (this.#state === 'loading') this.#state = 'idle';
    for (const off of this.#teardown) off();
    this.#teardown = [];
    super.disconnectedCallback();
  }
}

if (!customElements.get('echt-size-guide')) {
  customElements.define('echt-size-guide', EchtSizeGuide);
}
